import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import {
  Send, Loader2, MailCheck, CheckCircle, Gauge, Search, Smartphone,
  ShieldCheck, AlertTriangle,
} from 'lucide-react';
import { submitSeoCheck } from '../lib/api';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const CHECKS = [
  { icon: Gauge, label: 'Page speed', note: 'How fast it loads on a phone' },
  { icon: Search, label: 'Google visibility', note: 'Whether you can be found' },
  { icon: Smartphone, label: 'Mobile & meta', note: 'How it looks when shared' },
  { icon: ShieldCheck, label: 'Quick wins', note: 'The top things worth fixing' },
];

const StateCard = ({ icon: Icon, tone = 'accent', title, children }) => (
  <Card className="border-border/50">
    <CardContent className="p-8 md:p-12 text-center">
      <div className={`w-16 h-16 ${tone === 'warn' ? 'bg-amber-100' : 'bg-accent/10'} rounded-full flex items-center justify-center mx-auto mb-6`}>
        <Icon className={`w-8 h-8 ${tone === 'warn' ? 'text-amber-600' : 'text-accent'}`} />
      </div>
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">{title}</h2>
      <div className="text-muted-foreground max-w-md mx-auto">{children}</div>
    </CardContent>
  </Card>
);

const SeoCheck = () => {
  const [params, setParams] = useSearchParams();
  const confirmState = params.get('state'); // confirmed | unreachable | invalid

  const [form, setForm] = useState({ url: '', email: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const clearState = () => {
    const next = new URLSearchParams(params);
    next.delete('state');
    setParams(next, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) { setError('Please tick the box to confirm you’d like the report emailed to you.'); return; }
    setSubmitting(true);
    try {
      const url = /^https?:\/\//i.test(form.url.trim()) ? form.url.trim() : `https://${form.url.trim()}`;
      await submitSeoCheck({ url, email: form.email.trim(), consent: true });
      setSent(true);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      setError(
        code === 'RATE_LIMITED'
          ? 'That’s a few requests in a short time — please try again in a little while.'
          : err.response?.data?.error?.message || 'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ----- Post-confirmation landing states (from the email link redirect) -----
  const renderConfirmState = () => {
    if (confirmState === 'confirmed') {
      return (
        <StateCard icon={CheckCircle} title="You’re confirmed — report on its way">
          <p>Thanks! We’ve run the check and your report is landing in your inbox now
            (give it a minute or two). If it’s not there, check your spam folder.</p>
          <Button onClick={clearState} variant="outline" className="rounded-full mt-6">
            Check another site
          </Button>
        </StateCard>
      );
    }
    if (confirmState === 'unreachable') {
      return (
        <StateCard icon={AlertTriangle} tone="warn" title="We couldn’t reach that site">
          <p>We tried to load your site but couldn’t reach it — double-check the address
            (a typo, or the site being down, is usually the cause) and give it another go.</p>
          <Button onClick={clearState} className="rounded-full mt-6 bg-accent text-accent-foreground hover:bg-accent/90">
            Try again
          </Button>
        </StateCard>
      );
    }
    if (confirmState === 'retry') {
      return (
        <StateCard icon={AlertTriangle} tone="warn" title="Nearly there — couldn’t email it just now">
          <p>We ran your check, but couldn’t email the report this moment (a lot of emails
            to this address today, or a brief hiccup). Please try again shortly, or email{' '}
            <a href="mailto:support@docksidedesign.co.uk" className="text-accent hover:underline">support@docksidedesign.co.uk</a>{' '}
            and we’ll send it straight over.</p>
          <Button onClick={clearState} className="rounded-full mt-6 bg-accent text-accent-foreground hover:bg-accent/90">
            Try again
          </Button>
        </StateCard>
      );
    }
    // invalid / expired
    return (
      <StateCard icon={AlertTriangle} tone="warn" title="That link has expired">
        <p>Confirmation links last 24 hours. Pop your details in below and we’ll send a fresh one.</p>
        <Button onClick={clearState} className="rounded-full mt-6 bg-accent text-accent-foreground hover:bg-accent/90">
          Request a new report
        </Button>
      </StateCard>
    );
  };

  return (
    <div data-testid="seo-check-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-accent mb-4">
              Free · No obligation
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary mb-6">
              How healthy is your website?
            </h1>
            <p className="text-lg text-muted-foreground">
              Get a free health check that scores your site out of 100 and lists the top
              things to fix — in plain English, no jargon. We’ll email you the full report.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form / states */}
      <section className="pb-8 md:pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {confirmState ? (
            <motion.div {...fadeInUp}>{renderConfirmState()}</motion.div>
          ) : sent ? (
            <motion.div {...fadeInUp}>
              <StateCard icon={MailCheck} title="Almost there — check your inbox">
                <p>We’ve sent a confirmation link to <strong className="text-foreground">{form.email}</strong>.
                  Click it and we’ll run the check and email your report straight over.</p>
                <p className="text-sm mt-4">Didn’t get it? Check spam, or
                  {' '}<button onClick={() => setSent(false)} className="text-accent hover:underline">try again</button>.</p>
              </StateCard>
            </motion.div>
          ) : (
            <motion.div {...fadeInUp}>
              <Card className="border-border/50">
                <CardContent className="p-6 md:p-8">
                  <form onSubmit={handleSubmit} data-testid="seo-check-form">
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="url">Your website *</Label>
                        <Input
                          id="url" name="url" data-testid="seo-check-url-input"
                          value={form.url} onChange={change}
                          placeholder="yourbusiness.co.uk" required className="rounded-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Where should we send the report? *</Label>
                        <Input
                          id="email" name="email" type="email" data-testid="seo-check-email-input"
                          value={form.email} onChange={change}
                          placeholder="you@yourbusiness.co.uk" required className="rounded-lg"
                        />
                      </div>
                      <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
                        <input
                          type="checkbox" data-testid="seo-check-consent"
                          checked={consent} onChange={(e) => setConsent(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-input accent-accent shrink-0"
                        />
                        <span>
                          I’d like Dockside Design to email me this free report, and I confirm
                          I’m happy to be contacted about it. We’ll only use your email for this —
                          see our <a href="/privacy" className="text-accent hover:underline">Privacy Policy</a>.
                        </span>
                      </label>
                    </div>

                    {error && (
                      <div className="mt-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {error}
                      </div>
                    )}

                    <div className="mt-6">
                      <Button
                        type="submit" disabled={submitting} data-testid="seo-check-submit"
                        className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full py-6 text-lg"
                      >
                        {submitting ? (
                          <>Sending… <Loader2 className="ml-2 w-5 h-5 animate-spin" /></>
                        ) : (
                          <>Get my free report <Send className="ml-2 w-5 h-5" /></>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4 text-center">
                      We send one confirmation email first — the report only goes out once you confirm.
                      No spam, ever.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </section>

      {/* What we check */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h2 {...fadeInUp} className="font-heading text-3xl md:text-4xl font-bold text-primary text-center mb-12">
            What the check looks at
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CHECKS.map(({ icon: Icon, label, note }) => (
              <motion.div key={label} {...fadeInUp} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-accent" strokeWidth={1.5} />
                </div>
                <p className="font-semibold text-foreground mb-1">{label}</p>
                <p className="text-sm text-muted-foreground">{note}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeoCheck;
